import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Style from "./TabelaSaquesStyle";
import { AuthContext } from "../../context/AuthContext";
import { formatarMoeda, formatDateSystem } from "../../assets/utils";
import {
  FiChevronsLeft,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsRight,
} from "react-icons/fi";

const TabelaDeSaques = () => {
  const { withdraws } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [sortOrder, setSortOrder] = useState("date-desc");
  const [contractIdFilter, setContractIdFilter] = useState("");

  const processedSaques = useMemo(() => {
    let saquesFiltrados = withdraws.filter(
      (dado) => dado.description !== "Descontar" && dado.amountWithdrawn > 0
    );

    if (statusFilter !== "Todos") {
      saquesFiltrados = saquesFiltrados.filter(
        (saque) => saque.status === parseInt(statusFilter, 10)
      );
    }

    if (contractIdFilter) {
      saquesFiltrados = saquesFiltrados.filter((saque) =>
        saque.contractId?.toString().includes(contractIdFilter)
      );
    }

    const sortedSaques = [...saquesFiltrados].sort((a, b) => {
      switch (sortOrder) {
        case "date-asc":
          return new Date(a.dateCreated) - new Date(b.dateCreated);
        case "amount-desc":
          return b.amountWithdrawn - a.amountWithdrawn;
        case "amount-asc":
          return a.amountWithdrawn - b.amountWithdrawn;
        case "date-desc":
        default:
          return new Date(b.dateCreated) - new Date(a.dateCreated);
      }
    });

    return sortedSaques;
  }, [withdraws, statusFilter, sortOrder, contractIdFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, sortOrder, contractIdFilter]);

  const totalPages = Math.ceil(processedSaques.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = processedSaques.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handlePageInputChange = (e) => setPageInput(e.target.value);

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(pageInput);
    if (pageNumber && pageNumber > 0 && pageNumber <= totalPages) {
      paginate(pageNumber);
    }
    setPageInput("");
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "Pendente";
      case 2:
        return "Pago";
      case 3:
        return "Cancelado";
      case 4:
        return "Ctr. Recomprado";
      default:
        return "Desconhecido";
    }
  };

  const handleRowClick = (saqueId) => {
    navigate(`/saques/${saqueId}`);
  };

  const handleGoldenBoxWithdraw = (d, amount, contractId) => {
    const description = d ? d.toLowerCase() : "";

    if (description.includes("golden box") || description.includes("caixinha"))
      return `Transferência para Golden Box de R$${formatarMoeda(
        amount
      )} do contrato #${contractId}`;
    else if (description.includes("recompra")) {
      return `Saque para Recompra de R$${formatarMoeda(
        amount
      )} do contrato #${contractId}`;
    } else {
      return `Saque de R$${formatarMoeda(amount)} do contrato #${contractId}`;
    }
  };

  return (
    <>
      <Style.Filters>
        <Style.FilterBox>
          <span className="name">Filtrar por Contrato</span>
          <input
            className="input-box"
            type="number"
            placeholder="Digite o ID do contrato..."
            value={contractIdFilter}
            onChange={(e) => setContractIdFilter(e.target.value)}
          />
        </Style.FilterBox>
        <Style.FilterBox>
          <span className="name">Filtrar por Status</span>
          <select
            className="input-box"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="Todos">Todos</option>
            <option value="1">Pendente</option>
            <option value="2">Pago</option>
            <option value="3">Cancelado</option>
            <option value="4">Recomprado</option>
          </select>
        </Style.FilterBox>

        <Style.FilterBox>
          <span className="name">Ordenar por</span>
          <select
            className="input-box"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="date-desc">Mais Recentes</option>
            <option value="date-asc">Mais Antigos</option>
            <option value="amount-desc">Maior Valor</option>
            <option value="amount-asc">Menor Valor</option>
          </select>
        </Style.FilterBox>
      </Style.Filters>

      <Style.TabelaContainer>
        <Style.Tabela>
          <Style.TabelaHead>
            <Style.TabelaRow>
              <Style.TabelaHeader>ID</Style.TabelaHeader>
              <Style.TabelaHeader>Data Solicitação</Style.TabelaHeader>
              <Style.TabelaHeader>Descrição</Style.TabelaHeader>
              <Style.TabelaHeader>Valor</Style.TabelaHeader>
              <Style.TabelaHeader>Valor a Receber</Style.TabelaHeader>
              <Style.TabelaHeader>Status</Style.TabelaHeader>
              <Style.TabelaHeader>Data de Pagamento</Style.TabelaHeader>
            </Style.TabelaRow>
          </Style.TabelaHead>
          <Style.TabelaBody>
            {currentItems.length === 0 ? (
              <Style.TabelaRow>
                <Style.TabelaData
                  colSpan="7"
                  style={{ textAlign: "center", padding: "3rem" }}
                >
                  {processedSaques.length === 0 && withdraws.length > 0
                    ? "Nenhum saque encontrado com os filtros aplicados."
                    : "Ainda não há saques para exibir."}
                </Style.TabelaData>
              </Style.TabelaRow>
            ) : (
              currentItems.map((dado) => {
                const descBaixa = dado.description
                  ? dado.description.toLowerCase()
                  : "";
                const isGoldenBox =
                  descBaixa.includes("golden box") ||
                  descBaixa.includes("caixinha");

                return (
                  <Style.TabelaRow
                    key={dado.id}
                    onClick={() => handleRowClick(dado.id)}
                    isClickable
                    isGolden={isGoldenBox}
                  >
                    <Style.TabelaData>#{dado.id}</Style.TabelaData>
                    <Style.TabelaData>
                      {formatDateSystem(dado.dateCreated)}
                    </Style.TabelaData>
                    <Style.TabelaData>
                      {dado.sponsorWithdraw
                        ? `Saque de Indicação de R$${formatarMoeda(
                            dado.amountWithdrawn
                          )}`
                        : handleGoldenBoxWithdraw(
                            dado.description,
                            dado.amountWithdrawn,
                            dado.contractId
                          )}
                    </Style.TabelaData>
                    <Style.TabelaData>
                      R$ {formatarMoeda(dado.amountWithdrawn)}
                    </Style.TabelaData>
                    <Style.TabelaData>
                      R$ {formatarMoeda(dado.amountWithdrawn * 0.96)}
                    </Style.TabelaData>
                    <Style.TabelaData>
                      <Style.StatusBadge status={dado.status}>
                        {getStatusText(dado.status)}
                      </Style.StatusBadge>
                    </Style.TabelaData>
                    <Style.TabelaData>
                      {dado.paymentDate
                        ? formatDateSystem(dado.paymentDate)
                        : "Não Informado"}
                    </Style.TabelaData>
                  </Style.TabelaRow>
                );
              })
            )}
          </Style.TabelaBody>
        </Style.Tabela>
      </Style.TabelaContainer>

      {totalPages > 1 && (
        <Style.PaginationContainer>
          <Style.PaginationButton
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            title="Primeira Página"
          >
            <FiChevronsLeft />
          </Style.PaginationButton>
          <Style.PaginationButton
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            title="Página Anterior"
          >
            <FiChevronLeft />
          </Style.PaginationButton>
          <Style.PageInfo>
            Página {currentPage} de {totalPages}
          </Style.PageInfo>
          <form onSubmit={handlePageInputSubmit}>
            <Style.PageInput
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={handlePageInputChange}
              placeholder="Ir para..."
            />
          </form>
          <Style.PaginationButton
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Próxima Página"
          >
            <FiChevronRight />
          </Style.PaginationButton>
          <Style.PaginationButton
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            title="Última Página"
          >
            <FiChevronsRight />
          </Style.PaginationButton>
        </Style.PaginationContainer>
      )}
    </>
  );
};

export default TabelaDeSaques;
